import { Gamification } from './gamification.js';

// Firebase imports are now loaded dynamically

const DEFAULT_STATE = {
    avatar: {
        level: 1,
        exp: 0,
        distanceWalked: 0,
        visitedPlaces: []
    },
    currentDay: 1, // Legacy field, keeping for compatibility
    unlockedDays: [1], // Legacy
    completedDays: [], // Track completed POI IDs
    rewardsLog: {}, // Idempotent rewards registry (source of truth per eventi ricompensa)
    inventory: [],
    guestCode: null,
    selectedBnB: null,
    name: null,
    gender: 'male'
};

class StateManager {
    constructor() {
        this.state = this.loadLocalState() || JSON.parse(JSON.stringify(DEFAULT_STATE));
        this.state.rewardsLog = this.state.rewardsLog || {};
        this.currentUserRef = null;
        this.debounceTimer = null;

        // Auto-login from localStorage if previously logged in
        const savedUser = localStorage.getItem('lungro_latest_user');
        if (savedUser) {
            this.login(savedUser);
        }
    }

    loadLocalState() {
        const saved = localStorage.getItem('arberex_guest_state');
        return saved ? JSON.parse(saved) : null;
    }

    getState() {
        return this.state;
    }

    // --- AUTH & CLOUD SYNC ---

    isAuthenticated() {
        return !!this.state.guestCode || (window.authManager && window.authManager.isAuthenticated);
    }

    async initAdminSync(user) {
        if (!user) return;
        try {
            const { db } = await import('./firebase.js');
            const { doc } = await import('firebase/firestore');
            
            console.log(`Initializing Admin Sync for ${user.email}...`);
            this.currentUserRef = doc(db, "admin_progress", user.uid);
            
            // Sync initial state from cloud for admin
            await this.loadCloudState();
        } catch (e) {
            console.error("Admin Sync Init Error:", e);
        }
    }

    async loadCloudState() {
        if (!this.currentUserRef) {
            try {
                const { auth } = await import('./firebase.js');
                const currentUser = auth?.currentUser;
                const uid = currentUser?.uid || this.state.guestCode || localStorage.getItem('lungro_latest_user');
                if (uid) {
                    const { db } = await import('./firebase.js');
                    const { doc } = await import('firebase/firestore');
                    this.currentUserRef = doc(db, "users", uid);
                } else {
                    return;
                }
            } catch (e) {
                return;
            }
        }

        try {
            const { getDoc } = await import('firebase/firestore');
            const docSnap = await getDoc(this.currentUserRef);

            if (docSnap.exists()) {
                const cloudData = docSnap.data();
                console.log("Cloud State Loaded:", cloudData);
                
                // Deterministic reconciliation (Regole di Dominanza Semantica & Max-Reward Rule)
                // 1. Insiemi cumulativi: Unione di insiemi senza duplicati
                const mergedCompleted = Array.from(new Set([
                    ...(this.state.completedDays || []),
                    ...(cloudData.completedDays || [])
                ]));
                const mergedUnlocked = Array.from(new Set([
                    ...(this.state.unlockedDays || [1]),
                    ...(cloudData.unlockedDays || [1])
                ]));
                const mergedPlaces = Array.from(new Set([
                    ...(this.state.visitedPlaces || []),
                    ...(cloudData.visitedPlaces || []),
                    ...mergedCompleted
                ]));

                // 2. Riconciliazione deterministica rewardsLog (Max-Reward Rule)
                const localRewards = this.state.rewardsLog || {};
                const cloudRewards = cloudData.rewardsLog || {};
                const mergedRewardsLog = { ...localRewards };
                let newlyDiscoveredCloudRewardXP = 0;

                for (const [key, cloudEntry] of Object.entries(cloudRewards)) {
                    if (!cloudEntry || typeof cloudEntry !== 'object') continue;
                    const cloudAmount = Number(cloudEntry.amount || 0);

                    if (!mergedRewardsLog[key]) {
                        mergedRewardsLog[key] = {
                            amount: cloudAmount,
                            timestamp: cloudEntry.timestamp || new Date().toISOString(),
                            source: cloudEntry.source || 'cloud_sync'
                        };
                        newlyDiscoveredCloudRewardXP += cloudAmount;
                    } else {
                        const localAmount = Number(mergedRewardsLog[key].amount || 0);
                        if (cloudAmount > localAmount) {
                            mergedRewardsLog[key] = {
                                ...mergedRewardsLog[key],
                                amount: cloudAmount,
                                timestamp: cloudEntry.timestamp || mergedRewardsLog[key].timestamp
                            };
                            newlyDiscoveredCloudRewardXP += (cloudAmount - localAmount);
                        }
                    }
                }

                // Sincronizza retroattivamente le tappe completate nel rewardsLog per idempotenza universale
                mergedCompleted.forEach(id => {
                    const stageKey = `stage:${id}`;
                    if (!mergedRewardsLog[stageKey]) {
                        mergedRewardsLog[stageKey] = {
                            amount: 100,
                            timestamp: new Date().toISOString(),
                            source: 'migrated_stage'
                        };
                    }
                });

                // 3. Metriche monotone: massimo valore storico (preserva rigorosamente XP legacy senza azzeramenti)
                const localExp = Number(this.state.avatar?.exp || 0);
                const cloudExp = Number(cloudData.avatar?.exp || cloudData.stats?.xp || 0);
                const maxExp = Math.max(localExp, cloudExp, localExp + newlyDiscoveredCloudRewardXP);

                const localLevel = Number(this.state.avatar?.level || 1);
                const cloudLevel = Number(cloudData.avatar?.level || cloudData.stats?.level || 1);
                const calculatedLevel = Gamification.getLevelFromXP(maxExp);
                const maxLevel = Math.max(localLevel, cloudLevel, calculatedLevel);

                const localDist = Number(this.state.avatar?.distanceWalked || 0);
                const cloudDist = Number(cloudData.avatar?.distanceWalked || cloudData.stats?.distanceWalked || 0);
                const maxDist = Math.max(localDist, cloudDist);

                // 4. Profilo e sessione: preferenza locale se già valorizzato, altrimenti cloud
                this.state = {
                    ...this.state,
                    completedDays: mergedCompleted,
                    unlockedDays: mergedUnlocked,
                    visitedPlaces: mergedPlaces,
                    rewardsLog: mergedRewardsLog,
                    selectedBnB: this.state.selectedBnB || cloudData.selectedBnB || null,
                    name: this.state.name || cloudData.name || null,
                    gender: this.state.gender || cloudData.gender || 'male',
                    guestCode: this.state.guestCode || cloudData.guestCode || null,
                    avatar: {
                        ...(this.state.avatar || {}),
                        exp: maxExp,
                        level: maxLevel,
                        distanceWalked: maxDist,
                        visitedPlaces: mergedPlaces
                    }
                };

                this.saveStateLocally();
            }
        } catch (e) {
            console.warn("Error loading cloud state:", e);
        }
    }

    async syncOnReconnect() {
        if (typeof navigator !== 'undefined' && !navigator.onLine) return;
        try {
            console.log("🔄 Riconnessione rilevata: riconciliazione e sincronizzazione in corso...");
            if (this.currentUserRef || this.state.guestCode || (window.authManager && window.authManager.isAuthenticated) || localStorage.getItem('lungro_latest_user')) {
                await this.loadCloudState();
                await this.syncUserToCloud();
                console.log("✅ Riconciliazione offline/online completata con successo.");
            }
        } catch (err) {
            console.warn("Sync on reconnect warning:", err);
        }
    }

    async login(guestCode) {
        if (!guestCode) return;
        let normalizedCode = guestCode.trim().toUpperCase();

        // Provide better UX: auto-insert missing hyphen if user forgets it (e.g. ARB1234 -> ARB-1234)
        if (!normalizedCode.includes('-') && normalizedCode.length > 4) {
            const pre = normalizedCode.slice(0, -4);
            const suf = normalizedCode.slice(-4);
            normalizedCode = `${pre}-${suf}`;
        }

        try {
            const { db } = await import('./firebase.js');
            const { doc, getDoc, setDoc } = await import('firebase/firestore');

            const codeRef = doc(db, "guestCodes", normalizedCode);
            const codeSnap = await getDoc(codeRef);
            const isTestCode = normalizedCode.startsWith('TEST_');

            if (!codeSnap.exists() && !isTestCode) {
                localStorage.removeItem('lungro_latest_user');
                throw new Error("Codice Ospite non valido. Contatta la struttura per ottenerne uno.");
            }

            this.currentUserRef = doc(db, "users", normalizedCode);
            this.state.guestCode = normalizedCode;
            
            await this.loadCloudState();

            localStorage.setItem(`lungro_latest_user`, normalizedCode);
            this.saveState(); 

        } catch (e) {
            console.error("Login Error:", e);
            throw e;
        }
    }

    async logout() {
        try {
            // Prevent hanging if offline or if rules block
            await Promise.race([this.forceSave(), new Promise(r => setTimeout(r, 600))]);
        } catch (e) {
            console.warn("Save before logout skipped or failed", e);
        }

        if (window.authManager) {
            await window.authManager.logout();
        }

        this.state = JSON.parse(JSON.stringify(DEFAULT_STATE));
        this.currentUserRef = null;
        localStorage.removeItem('lungro_latest_user');
        localStorage.removeItem('arberex_guest_state'); // Clear local guest state too
    }

    // --- PERSISTENCE ---

    saveStateLocally() {
        localStorage.setItem('arberex_guest_state', JSON.stringify(this.state));
    }

    saveState() {
        this.saveStateLocally();
        if (!this.currentUserRef) return;

        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.forceSave(), 2000);
    }

    async forceSave() {
        this.saveStateLocally();
        await this.syncUserToCloud();
    }

    async syncUserToCloud() {
        try {
            const { db, auth } = await import('./firebase.js');
            const { doc, setDoc } = await import('firebase/firestore');

            const currentUser = auth?.currentUser;
            const uid = currentUser?.uid || this.state.guestCode || localStorage.getItem('lungro_latest_user');

            if (!uid) return;

            if (!this.currentUserRef) {
                this.currentUserRef = doc(db, "users", uid);
            }

            const displayName = currentUser?.displayName || this.state.name || (currentUser?.email ? currentUser.email.split('@')[0] : "Esploratore");

            const userData = {
                uid: uid,
                name: displayName,
                displayName: displayName,
                email: currentUser?.email || null,
                stats: {
                    xp: this.state.avatar.exp,
                    level: this.state.avatar.level,
                    distanceWalked: this.state.avatar.distanceWalked || 0,
                    visitedCount: (this.state.completedDays || []).length
                },
                avatar: this.state.avatar,
                selectedBnB: this.state.selectedBnB || null,
                completedDays: this.state.completedDays || [],
                rewardsLog: this.state.rewardsLog || {},
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, "users", uid), userData, { merge: true });
            console.log(`🏆 XP Classifica Sincronizzata in Cloud (${displayName}): ${this.state.avatar.exp} XP`);
        } catch (e) {
            console.warn("Cloud XP Sync Warning:", e);
        }
    }

    // --- ACTIONS & IDEMPOTENT REWARDS ---

    claimReward(rewardKey, amount, source = 'general') {
        if (!rewardKey || typeof rewardKey !== 'string') {
            console.warn("Invalid rewardKey passed to claimReward:", rewardKey);
            return { success: false, alreadyClaimed: false, amount: 0 };
        }

        this.state.rewardsLog = this.state.rewardsLog || {};

        if (this.state.rewardsLog[rewardKey]) {
            console.log(`[RewardsLog] Chiave "${rewardKey}" già presente. Accredito duplicato ignorato (idempotente).`);
            return {
                success: false,
                alreadyClaimed: true,
                amount: 0,
                reward: this.state.rewardsLog[rewardKey]
            };
        }

        const numAmount = Number(amount) || 0;
        this.state.rewardsLog[rewardKey] = {
            amount: numAmount,
            timestamp: new Date().toISOString(),
            source: source
        };

        this.addExperience(numAmount);
        return {
            success: true,
            alreadyClaimed: false,
            amount: numAmount,
            rewardKey: rewardKey
        };
    }

    hasReward(rewardKey) {
        return !!(this.state.rewardsLog && this.state.rewardsLog[rewardKey]);
    }

    getRewardsLog() {
        return this.state.rewardsLog || {};
    }

    setBnB(bnbId, startLevelId) {
        if (this.state.selectedBnB !== bnbId) {
            this.state.selectedBnB = bnbId;

            // Reset Progress on Route Change/Selection
            if (startLevelId) {
                this.state.completedDays = [];
                this.state.unlockedDays = [startLevelId];
                this.state.visitedPlaces = [];
                console.log(`Route Reset! Starting at Level ${startLevelId}`);
            }
        } else {
            console.log("Resuming existing B&B session...");
        }

        this.saveState();
    }

    setSelectedBnB(bnbId, startLevelId) {
        this.setBnB(bnbId, startLevelId);
    }

    startJourney() {
        // Just ensures initial state is robust
        if (!this.state.completedDays) this.state.completedDays = [];
        this.saveState();
    }

    getSelectedBnB() {
        return this.state.selectedBnB;
    }

    completeDay(levelId) {
        this.state.completedDays = this.state.completedDays || [];
        this.state.rewardsLog = this.state.rewardsLog || {};
        const stageKey = `stage:${levelId}`;

        if (this.state.completedDays.includes(levelId) || this.state.rewardsLog[stageKey]) {
            if (!this.state.completedDays.includes(levelId)) {
                this.state.completedDays.push(levelId);
                this.saveStateLocally();
            }
            return false; // Already done (idempotente)
        }

        this.state.completedDays.push(levelId);
        this.state.visitedPlaces = this.state.visitedPlaces || [];
        if (!this.state.visitedPlaces.includes(levelId)) {
            this.state.visitedPlaces.push(levelId);
        }

        this.state.rewardsLog[stageKey] = {
            amount: 100,
            timestamp: new Date().toISOString(),
            source: 'stage_completion'
        };

        this.saveState();
        return true; // New completion
    }

    unlockDay(levelId) {
        if (!this.state.unlockedDays.includes(levelId)) {
            this.state.unlockedDays.push(levelId);
            this.saveState();
            console.log(`🔓 Day ${levelId} UNLOCKED!`);
        }
    }

    addExperience(amount, rewardKey = null, source = 'manual') {
        if (rewardKey) {
            return this.claimReward(rewardKey, amount, source);
        }

        const numAmount = Number(amount) || 0;
        this.state.avatar.exp += numAmount;

        // Use Gamification Engine
        const currentLevel = this.state.avatar.level;
        const newLevel = Gamification.getLevelFromXP(this.state.avatar.exp);

        if (newLevel > currentLevel) {
            this.state.avatar.level = newLevel;
            if (typeof alert === 'function') {
                alert(`🎉 LEVEL UP! Sei ora al livello ${newLevel}!`);
            }
        }

        this.saveState();
        this.syncUserToCloud().catch(e => console.warn("XP Sync Error:", e));
        return { success: true, amount: numAmount };
    }

    addDistance(meters) {
        this.state.avatar.distanceWalked += meters;
        this.saveState();
    }

    updateProfile(name, gender) {
        this.state.name = name;
        this.state.gender = gender;
        this.saveState();
        this.syncUserToCloud().catch(e => console.warn("Profile Sync Error:", e));
    }

    updateState(partialState = {}) {
        if (!partialState || typeof partialState !== 'object') return;

        // Support direct 'xp' property mapping to avatar.exp
        if (typeof partialState.xp === 'number') {
            this.state.avatar = this.state.avatar || { level: 1, exp: 0, distanceWalked: 0, visitedPlaces: [] };
            this.state.avatar.exp = partialState.xp;
        }

        if (partialState.avatar) {
            this.state.avatar = {
                ...(this.state.avatar || {}),
                ...partialState.avatar
            };
        }

        // Merge remaining fields
        for (const [key, value] of Object.entries(partialState)) {
            if (key !== 'avatar' && key !== 'xp') {
                this.state[key] = value;
            }
        }

        this.saveState();
        this.syncUserToCloud().catch(e => console.warn("Cloud Sync Warning on updateState:", e));
    }
}

export const stateManager = new StateManager();
